import Hls from 'hls.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import LiveTvBadge from './LiveTvBadge';
import { LiveTvPlaybackStatus } from '../types/liveTv.types';

interface LiveTvPlayerProps {
  sources: string[];
  poster?: string;
  onFatalError?: () => void;
}

interface LiveTvPlayerStatusInfo {
  status: LiveTvPlaybackStatus;
  error: string | null;
}

const FALLBACK_HTTP_CODES = new Set([
  401, // Unauthorized
  403, // Forbidden / geo / hotlink
  404, // Not found
  410, // Gone
  451, // Unavailable for legal reasons
  500,
  502,
  503,
  504,
  520,
  521,
  522,
  523,
  524,
]);

function getStatusLabel(status: LiveTvPlaybackStatus) {
  switch (status) {
    case 'live':
      return {
        text: 'LIVE',
        background: 'rgba(255,59,48,0.18)',
        color: '#ffb3ad',
      };
    case 'paused':
      return {
        text: 'PAUSED',
        background: '#ececef',
        color: '#1f1f21',
      };
    case 'timeshifted':
      return {
        text: 'NOT LIVE',
        background: 'var(--walky-orange-soft)',
        color: 'var(--walky-orange)',
      };
    case 'loading':
      return {
        text: 'LOADING',
        background: 'var(--walky-orange-soft)',
        color: 'var(--walky-orange)',
      };
    case 'error':
      return {
        text: 'ERROR',
        background: 'rgba(255,59,48,0.18)',
        color: '#ffb3ad',
      };
    default:
      return null;
  }
}

function getHlsResponseCode(data: unknown): number | undefined {
  const maybeData = data as {
    response?: {
      code?: number;
    };
    context?: {
      response?: {
        code?: number;
      };
    };
    networkDetails?: {
      status?: number;
    };
  };

  return maybeData.response?.code || maybeData.context?.response?.code || maybeData.networkDetails?.status;
}

function shouldFallbackFastFromDetails(details: string) {
  return (
    details.includes('manifestLoadError') ||
    details.includes('manifestLoadTimeOut') ||
    details.includes('levelLoadError') ||
    details.includes('levelLoadTimeOut') ||
    details.includes('fragLoadError') ||
    details.includes('fragLoadTimeOut') ||
    details.includes('keyLoadError') ||
    details.includes('keyLoadTimeOut')
  );
}

function isAutoplayBlockedError(error: unknown) {
  const maybeError = error as { name?: string; message?: string };
  const name = maybeError?.name || '';
  const message = (maybeError?.message || '').toLowerCase();

  return (
    name === 'NotAllowedError' ||
    message.includes("user didn't interact") ||
    message.includes('play() failed because') ||
    message.includes('request is not allowed')
  );
}

export default function LiveTvPlayer({ sources, poster, onFatalError }: LiveTvPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [statusInfo, setStatusInfo] = useState<LiveTvPlayerStatusInfo>({
    status: 'idle',
    error: null,
  });

  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);

  const initialLiveSyncDoneRef = useRef(false);
  const userHasManuallySeekedRef = useRef(false);
  const fallbackTriggeredRef = useRef(false);

  const currentSource = sources[currentSourceIndex];

  useEffect(() => {
    setCurrentSourceIndex(0);
    initialLiveSyncDoneRef.current = false;
    userHasManuallySeekedRef.current = false;
    fallbackTriggeredRef.current = false;
    setStatusInfo({
      status: sources.length > 0 ? 'loading' : 'error',
      error: sources.length > 0 ? null : 'No IPTV source available',
    });
  }, [sources]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !currentSource) {
      if (!currentSource) {
        setStatusInfo({
          status: 'error',
          error: 'No IPTV source available',
        });
        onFatalError?.();
      }

      return;
    }

    let hls: Hls | null = null;
    let interval: number | null = null;
    let destroyed = false;
    let errorCount = 0;

    const setStatus = (status: LiveTvPlaybackStatus, error: string | null = null) => {
      if (destroyed) return;
      setStatusInfo({ status, error });
    };

    const cleanupMedia = () => {
      if (interval) {
        window.clearInterval(interval);
        interval = null;
      }

      if (hls) {
        hls.destroy();
        hls = null;
      }

      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    const triggerFallback = (reason?: string) => {
      if (destroyed) return;
      if (fallbackTriggeredRef.current) return;

      fallbackTriggeredRef.current = true;

      const fallbackReason = reason || 'No working IPTV source available';

      console.warn('LiveTvPlayer fallback:', fallbackReason);

      setStatus('error', fallbackReason);
      cleanupMedia();
      onFatalError?.();
    };

    const tryNextSourceOrFallback = (reason?: string) => {
      if (destroyed) return;

      if (currentSourceIndex < sources.length - 1) {
        console.warn(`LiveTvPlayer trying next IPTV source after error: ${reason || 'Unknown error'}`);

        cleanupMedia();

        setCurrentSourceIndex((prev) => prev + 1);
        initialLiveSyncDoneRef.current = false;
        userHasManuallySeekedRef.current = false;
        fallbackTriggeredRef.current = false;
        setStatus('loading');

        return;
      }

      triggerFallback(reason);
    };

    const jumpToLiveEdge = () => {
      const seekable = video.seekable;

      if (seekable && seekable.length > 0) {
        const liveEdge = seekable.end(seekable.length - 1);
        const safeTarget = Math.max(0, liveEdge - 1);
        video.currentTime = safeTarget;
      }
    };

    const syncToLiveOnce = () => {
      if (initialLiveSyncDoneRef.current) return;
      if (userHasManuallySeekedRef.current) return;

      jumpToLiveEdge();
      initialLiveSyncDoneRef.current = true;
    };

    const updateLiveStatus = () => {
      if (destroyed) return;

      if (video.paused) {
        setStatus('paused');
        return;
      }

      const seekable = video.seekable;

      if (!seekable || seekable.length === 0) {
        setStatus('live');
        return;
      }

      const liveEdge = seekable.end(seekable.length - 1);
      const distance = liveEdge - video.currentTime;

      if (distance > 8) {
        setStatus('timeshifted');
      } else {
        setStatus('live');
      }
    };

    const handleVideoPlayError = (reason: string, error: unknown) => {
      if (isAutoplayBlockedError(error)) {
        console.info('LiveTvPlayer autoplay blocked; waiting for user interaction.', error);
        setStatus('paused');
        return;
      }

      tryNextSourceOrFallback(reason);
    };

    const playVideo = (reasonOnFail: string) => {
      video.play().catch((error) => {
        handleVideoPlayError(reasonOnFail, error);
      });
    };

    const isHls = currentSource.toLowerCase().includes('.m3u8');

    setStatus('loading');

    if (isHls && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,

        // Un poco más agresivo para no quedarse eternamente colgado.
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 1,
        manifestLoadingRetryDelay: 1000,

        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 1,
        levelLoadingRetryDelay: 1000,

        fragLoadingTimeOut: 12000,
        fragLoadingMaxRetry: 1,
        fragLoadingRetryDelay: 1000,
      });

      hls.loadSource(currentSource);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        playVideo('Autoplay failed on IPTV source');
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (destroyed) return;

        const responseCode = getHlsResponseCode(data);
        const details = String(data.details || '');
        const errorType = String(data.type || '');

        const isNetworkError = data.type === Hls.ErrorTypes.NETWORK_ERROR;
        const isMediaError = data.type === Hls.ErrorTypes.MEDIA_ERROR;

        if (responseCode && FALLBACK_HTTP_CODES.has(responseCode)) {
          tryNextSourceOrFallback(`HLS HTTP error ${responseCode}`);
          return;
        }

        if (shouldFallbackFastFromDetails(details)) {
          errorCount += 1;
        }

        if (data.fatal) {
          console.error('HLS fatal error:', data);

          if (isMediaError) {
            try {
              hls?.recoverMediaError();
              return;
            } catch {
              tryNextSourceOrFallback('Fatal HLS media error');
              return;
            }
          }

          if (isNetworkError) {
            tryNextSourceOrFallback(
              responseCode
                ? `Fatal HLS network error ${responseCode}`
                : `Fatal HLS network error: ${details || errorType}`,
            );
            return;
          }

          tryNextSourceOrFallback(`Fatal HLS error: ${details || errorType || 'unknown'}`);
          return;
        }

        if (isNetworkError) {
          errorCount += 1;

          const shouldFallbackFast =
            shouldFallbackFastFromDetails(details) || Boolean(responseCode && FALLBACK_HTTP_CODES.has(responseCode));

          if (shouldFallbackFast || errorCount >= 2) {
            tryNextSourceOrFallback(
              responseCode
                ? `HLS network error ${responseCode}`
                : `HLS network/CORS/load error: ${details || errorType || 'unknown'}`,
            );
          }
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari/iOS puede reproducir HLS nativo.
      video.src = currentSource;
      playVideo('Native HLS playback failed');
    } else {
      video.src = currentSource;
      playVideo('Native video playback failed');
    }

    const onPlay = () => {
      updateLiveStatus();
    };

    const onPause = () => {
      setStatus('paused');
    };

    const onTimeUpdate = () => {
      updateLiveStatus();
    };

    const onLoadedMetadata = () => {
      syncToLiveOnce();
      updateLiveStatus();
    };

    const onCanPlay = () => {
      syncToLiveOnce();
      updateLiveStatus();
    };

    const onWaiting = () => {
      setStatus('loading');
    };

    const onStalled = () => {
      setStatus('loading');
    };

    const onSeeking = () => {
      userHasManuallySeekedRef.current = true;
      updateLiveStatus();
    };

    const onSeeked = () => {
      userHasManuallySeekedRef.current = true;
      updateLiveStatus();
    };

    const onError = () => {
      const mediaError = video.error;
      const code = mediaError?.code;
      const message = mediaError?.message;

      tryNextSourceOrFallback(code ? `Video element error ${code}: ${message || 'unknown'}` : 'Video element error');
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    interval = window.setInterval(() => {
      updateLiveStatus();
    }, 3000);

    return () => {
      destroyed = true;

      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);

      cleanupMedia();
    };
  }, [currentSource, currentSourceIndex, onFatalError, sources]);

  const badge = useMemo(() => getStatusLabel(statusInfo.status), [statusInfo.status]);

  const handleGoLive = () => {
    const video = videoRef.current;
    if (!video) return;

    userHasManuallySeekedRef.current = false;

    const seekable = video.seekable;

    if (seekable && seekable.length > 0) {
      const liveEdge = seekable.end(seekable.length - 1);
      video.currentTime = Math.max(0, liveEdge - 1);

      video.play().catch((error) => {
        if (isAutoplayBlockedError(error)) {
          setStatusInfo({
            status: 'paused',
            error: null,
          });
          return;
        }

        setStatusInfo({
          status: 'error',
          error: 'Unable to resume live playback',
        });
      });
    }

    setStatusInfo({
      status: 'live',
      error: null,
    });
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}>
        {badge && (
          <LiveTvBadge background={badge.background} color={badge.color}>
            {badge.text}
          </LiveTvBadge>
        )}

        {statusInfo.status === 'timeshifted' && (
          <LiveTvBadge background="var(--walky-charcoal)" color="#ffffff" asButton onClick={handleGoLive}>
            Go Live
          </LiveTvBadge>
        )}

        {currentSourceIndex > 0 && (
          <LiveTvBadge background="var(--walky-neutral-soft)" color="var(--walky-ink)">
            Source {currentSourceIndex + 1}/{sources.length}
          </LiveTvBadge>
        )}
      </div>

      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          background: '#000',
          borderRadius: 8,
          borderTop: '4px solid var(--walky-orange)',
          overflow: 'hidden',
          boxShadow: 'var(--walky-shadow-strong)',
        }}>
        <video
          ref={videoRef}
          controls
          autoPlay
          playsInline
          poster={poster}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            background: '#000',
          }}
        />
      </div>

      {statusInfo.error && (
        <div
          style={{
            marginTop: 10,
            color: 'var(--walky-error-text)',
            background: 'var(--walky-error-bg)',
            borderLeft: '3px solid var(--walky-error-border)',
            padding: '10px 12px',
            fontSize: 14,
          }}>
          {statusInfo.error}
        </div>
      )}
    </div>
  );
}
